import { gql } from '@apollo/client'

import {
  FINANCIALS_FRAGMENT,
  OFFER_FRAGMENT,
  PRIVATE_POST_OFFER_FRAGMENT,
  PUBLIC_POST_FRAGMENT,
  PUBLIC_POST_WITH_OFFERS_FRAGMENT,
  RATES_FRAGMENT
} from '../fragments'

// Graphql query to get the workspace's posts.
export const PRIVATE_WORKSPACE_POSTS = gql`
  query PrivateWorkspacePosts($input: PostInput) {
    privateWorkspacePosts(input: $input) {
      paginationData {
        dataSource
        hasNextPage
        paginationKey
      }
      posts {
        ...PostFields
        assignedReps {
          removed
          role
          user {
            id
            name
            email
            phone
            photo
          }
        }
        distance {
          unit
          value
        }
        tmsURL
        customer {
          name
        }
        offers {
          ...OfferFragment
        }
        financials {
          customerQuoteTotal
          customerChargeTotal
        }
        greenscreensRates {
          ...RatesFields
        }
        tmsRates {
          ...RatesFields
        }
        hasPendingOffers
        created
        areAllOffersRespondedTo
        isLastMessageSentByPostOwner
        lastMessageDateTime
        lastMessageSentByUserID
        lastMessageText
        lastMessageSentByUser {
          id
          name
          email
          phone
          photo
        }
      }
    }
  }
  ${PUBLIC_POST_FRAGMENT}
  ${OFFER_FRAGMENT}
  ${RATES_FRAGMENT}
`

// Graphql query to get the workspace's posts by load number.
export const PRIVATE_WORKSPACE_POSTS_BY_LOAD_NUMBER = gql`
  query PrivateWorkspacePostsSearch($workspaceID: ID!, $loadNumber: String) {
    privateWorkspacePostsSearch(input: { workspaceID: $workspaceID, loadNumber: $loadNumber }) {
      ...PostFields
      distance {
        unit
        value
      }
      tmsURL
      customer {
        name
      }
      offers {
        ...OfferFragment
      }
      financials {
        customerQuoteTotal
        customerChargeTotal
      }
      greenscreensRates {
        ...RatesFields
      }
      tmsRates {
        ...RatesFields
      }
      hasPendingOffers
      created
      isLastMessageSentByPostOwner
      lastMessageDateTime
      lastMessageSentByUserID
      lastMessageText
      lastMessageSentByUser {
        id
        name
        email
        phone
        photo
      }
      areAllOffersRespondedTo
    }
  }
  ${PUBLIC_POST_FRAGMENT}
  ${OFFER_FRAGMENT}
  ${RATES_FRAGMENT}
`

export const PRIVATE_POST_WITH_OFFERS = gql`
  query Post($id: ID!) {
    post(input: { id: $id }) {
      ...PostFields

      created
      customer {
        name
      }
      financials {
        ...FinancialsFields
      }
      cargoChiefRates {
        ...RatesFields
      }
      freightWavesSonarRates {
        ...RatesFields
      }
      greenscreensRates {
        ...RatesFields
      }
      deleted
      hasPendingOffers
      offers {
        ...PrivatePostOfferFragment
      }
      shipmentID
      tmsRates {
        ...RatesFields
      }
      tmsURL
      isPostOwner
      isLastMessageSentByPostOwner
      areAllOffersRespondedTo
    }
  }
  ${RATES_FRAGMENT}
  ${FINANCIALS_FRAGMENT}
  ${PUBLIC_POST_FRAGMENT}
  ${PRIVATE_POST_OFFER_FRAGMENT}
`

// Graphql query to get the user's posts.
export const PRIVATE_USER_POSTS = gql`
  query PrivateUserPosts($input: PostInput) {
    privateUserPosts(input: $input) {
      paginationData {
        dataSource
        hasNextPage
        paginationKey
      }
      posts {
        ...PostFields
        distance {
          unit
          value
        }
        tmsURL
        customer {
          name
        }
        offers {
          ...OfferFragment
        }
        financials {
          customerQuoteTotal
          customerChargeTotal
        }
        greenscreensRates {
          ...RatesFields
        }
        tmsRates {
          ...RatesFields
        }
        hasPendingOffers
        created
      }
    }
  }
  ${PUBLIC_POST_FRAGMENT}
  ${OFFER_FRAGMENT}
  ${RATES_FRAGMENT}
`

export const PUBLIC_POST_WITH_OFFERS = gql`
  query Post($id: ID!) {
    post(input: { id: $id }) {
      ...PublicPostWithOffersFields
      deleted
      created
      offers {
        ...OfferFragment
      }
      isPostOwner
    }
  }
  ${PUBLIC_POST_WITH_OFFERS_FRAGMENT}
  ${OFFER_FRAGMENT}
`

// GraphQL query to get public posts for a workspace
export const GET_PUBLIC_WORKSPACE_POSTS = gql`
  query GetPublicWorkspacePosts($input: PublicPostInput) {
    publicWorkspacePosts(input: $input) {
      paginationData {
        hasNextPage
        hasPreviousPage
        totalCount
        dataSource
      }
      posts {
        ...PostFields
        created
      }
    }
  }
  ${PUBLIC_POST_FRAGMENT}
`

export const CARRIER_HUB_POSTS = gql`
  query CarrierHubPosts($input: CarrierHubPostsQueryInput) {
    carrierHubPosts(input: $input) {
      totalCount
      paginationData {
        dataSource
        hasNextPage
        paginationKey
      }
      posts {
        id
        loadNumber
        postStatus
        created
        commodity
        description
        origin {
          city
          state
          stateCode
          coordinates {
            latitude
            longitude
          }
        }
        destination {
          city
          state
          stateCode
          coordinates {
            latitude
            longitude
          }
        }
        originAppointment {
          start {
            date
            time
            timezone
          }
          end {
            date
            time
            timezone
          }
          isFirstComeFirstServe
        }
        destinationAppointment {
          start {
            date
            time
            timezone
          }
          end {
            date
            time
            timezone
          }
          isFirstComeFirstServe
        }
        truckType
        equipmentTags
        weight {
          value
          unit
        }
        distance {
          value
          unit
        }
        isHazmat
        isTeam
        isTempControlled
        numberOfPicks
        numberOfDrops
        stops {
          id
          sequence
          type
          warehouse {
            name
            address {
              city
              state
              stateCode
            }
          }
        }
        workspace {
          id
          workspacename
        }
      }
    }
  }
`

export const CARRIER_HUB_TRUCK_TYPE_FILTER = gql`
  query CarrierHubTruckTypeFilter($input: CarrierHubTruckTypeFilterInput!) {
    carrierHubTruckTypeFilter(input: $input) {
      count
      label
      value
    }
  }
`
